import {
  AdminSetPasswordPayload,
  PasswordValidationRequest,
  SetPasswordPayload,
  UserAuthPayload,
  UserAuthResult,
  UserProfileResult,
  ValidatePasswordResponse,
} from '@/models/dto'
import { bffProxyUrl } from '@/utils/env'
import { AxiosResponse } from 'axios'
import Vue from 'vue'

export default {
  /**
   * Performs a login request to the API with the provided credentials.
   *
   * @param payload - An object containing the login credentials.
   * @returns A promise that resolves to an AxiosResponse with the login result.
   */
  login(payload: UserAuthPayload): Promise<AxiosResponse<UserAuthResult>> {
    const url = `${bffProxyUrl}/auth/a3`
    return Vue.prototype.$http.post(url, payload)
  },
  /**
   * Retrieves the user's profile information from the API.
   *
   * @returns A promise that resolves to an AxiosResponse with the user's profile information.
   */
  getUserProfile(): Promise<AxiosResponse<UserProfileResult>> {
    const url = `${bffProxyUrl}/userProfile`
    return Vue.prototype.$http.get(url)
  },
  /**
   * Retrieves the user's profile information from the API using the JWT token.
   *
   * @returns A promise that resolves to an AxiosResponse with the user's profile information.
   */
  getUserProfileWithToken(): Promise<AxiosResponse<UserAuthResult>> {
    const url = `${bffProxyUrl}/auth/jwt`
    return Vue.prototype.$http.post(url, {})
  },
  /**
   * Changes the password of the currently logged in user
   *
   * @param payload - An object containing the new password and the user's ID
   * @returns A promise that resolves to an AxiosResponse
   */
  setPassword(payload: SetPasswordPayload): Promise<AxiosResponse<void>> {
    const url = `${bffProxyUrl}/v2/password/set`
    return Vue.prototype.$http.post(url, payload)
  },
  /**
   * Changes the password of a user as an administrator
   *
   * @param payload - An object containing the new password and the user's ID
   * @returns A promise that resolves to an AxiosResponse
   */
  adminSetPassword(
    payload: AdminSetPasswordPayload
  ): Promise<AxiosResponse<void>> {
    const url = `${bffProxyUrl}/v2/password/set/admin`
    return Vue.prototype.$http.post(url, payload)
  },
  /**
   * Validates password against endpoint in Auth service
   *
   * @param password - new password chosen by the user
   * @returns A promise with the result of validation
   */
  validatePassword(
    password: string
  ): Promise<AxiosResponse<ValidatePasswordResponse>> {
    const url = `${bffProxyUrl}/v2/password/validate`
    const payload: PasswordValidationRequest = { password }
    return Vue.prototype.$http.post(url, payload)
  },
}
